import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  StarReviewGraph,
  Reviews,
  SubmissionModal,
  CONTEXT,
} from '@nexus/fff-ratings-and-reviews';

import env from 'utils/env';
import PageContainer from 'shared-components/PageContainer/PageContainer';
import RatingsAndReviewsHeader from './RatingsAndReviewsHeader/RatingsAndReviewsHeader';
import EmptyReviews from './EmptyReviews/EmptyReviews';
import ProductReviewCTA from '../ProductReviewCTA';

import styles from './RatingsAndReviews.module.scss';

const RatingsAndReviews = forwardRef(
  (
    {
      ratingGraph,
      productId,
      productBrand,
      productName,
      loggedIn,
      productHasReviewed,
      hasReviews,
      sku,
      imgUrl,
      updateReviewedProducts,
    },
    ref
  ) => {
    const [modalOpen, setModalOpen] = useState(false);

    const openModal = () => setModalOpen(true);

    const closeModal = () => setModalOpen(false);

    const handleReviewSuccess = (response) => {
      const data = response?.data?.items?.[0];
      const payload = { productId: data?.productId, hasReviewed: true };
      updateReviewedProducts(payload);
    };

    const { averageRating, totalReviewCount, ratingDistribution } = ratingGraph;

    return (
      <>
        <PageContainer>
          <div
            ref={ref}
            className={styles.reviews}
            id="rate-and-review-container"
          >
            <RatingsAndReviewsHeader
              rating={averageRating}
              count={totalReviewCount}
            />
            {totalReviewCount > 0 ? (
              <div className={styles.reviewsDetails}>
                <div className={styles.reviewsGraph}>
                  <StarReviewGraph
                    averageRating={averageRating}
                    totalReviewCount={totalReviewCount}
                    ratingDistribution={ratingDistribution}
                  />
                  <ProductReviewCTA
                    loggedIn={loggedIn}
                    productHasReviewed={productHasReviewed}
                    productHasReviews={hasReviews}
                    ctaType="button"
                    openReviewModal={openModal}
                  />
                </div>
                {productId && (
                  <div className={styles.reviewsList}>
                    <Reviews
                      productId={productId}
                      brandName={productBrand}
                      productName={productName}
                      env={env.appEnv}
                      isLoggedIn={loggedIn}
                    />
                  </div>
                )}
              </div>
            ) : (
              <EmptyReviews>
                <ProductReviewCTA
                  loggedIn={loggedIn}
                  productHasReviewed={productHasReviewed}
                  productHasReviews={hasReviews}
                  ctaType="button"
                  openReviewModal={openModal}
                />
              </EmptyReviews>
            )}
          </div>
        </PageContainer>
        <SubmissionModal
          isOpen={modalOpen}
          brandName={productBrand}
          name={productName}
          imgUrl={imgUrl}
          productId={productId}
          sku={sku}
          context={CONTEXT.pdp}
          env={env.appEnv}
          closeModal={closeModal}
          onSuccess={handleReviewSuccess}
        />
      </>
    );
  }
);

RatingsAndReviews.propTypes = {
  ratingGraph: PropTypes.shape({
    averageRating: PropTypes.number,
    totalReviewCount: PropTypes.number,
    ratingDistribution: PropTypes.shape({
      [PropTypes.number]: PropTypes.number,
    }),
  }),
  productId: PropTypes.number.isRequired,
  productBrand: PropTypes.string.isRequired,
  productName: PropTypes.string.isRequired,
  loggedIn: PropTypes.bool.isRequired,
  productHasReviewed: PropTypes.bool.isRequired,
  hasReviews: PropTypes.bool.isRequired,
  sku: PropTypes.string.isRequired,
  imgUrl: PropTypes.string.isRequired,
  updateReviewedProducts: PropTypes.func.isRequired,
};

RatingsAndReviews.defaultProps = {
  ratingGraph: {
    averageRating: 0,
    totalReviewCount: 0,
    ratingDistribution: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    },
  },
};

export default RatingsAndReviews;
